import React, { useRef, useState } from 'react';
import { Badge, Button, Card, CardBody, Spinner } from 'reactstrap';

import { AppTile } from './rowParts';

const price = value => {
  if (String(value).trim() === '') return null;
  const amount = Number(value);
  if (Number.isNaN(amount)) return null;
  return amount === 0 ? 'Free' : `$${amount.toFixed(2)}/mo`;
};

/**
 * Live App Store preview (AC3): the listing card as the store admin will see it, re-rendered from the
 * form state on every change — image, name, author, description and tags. It reads the form, never the
 * persisted advert, which is the whole point: it shows what Save would publish.
 *
 * The icon tile is also where the icon is uploaded (product feedback, UN-3248): hover it and click, the
 * way most app consoles do it, instead of a file input further down the form. `onUpload` receives the
 * change event of the hidden input, the same shape the old file input produced.
 */
const AdvertPreview = ({ form, onUpload, onRemove, uploading = false, disabled = false }) => {
  const input = useRef(null);
  const [hover, setHover] = useState(false);
  const canUpload = !!onUpload && !disabled;
  const pick = () => {
    if (canUpload && input.current) input.current.click();
  };
  return (
    <Card className="mb-3">
      <CardBody>
        <div className="text-muted text-uppercase mb-2" style={{ fontSize: 11, letterSpacing: '.05em' }}>
          App Store preview
        </div>
        {/* gap-3, for the same reason as the store-preview modal (UN-3372 item 9): the 56px icon tile sat
            flush against the name/author/price block. A flex gap rather than a margin on the tile, so it
            survives the wrap at narrow widths instead of leaving the text against the tile's edge again.
            This is the sidebar panel on the form — the one the report's screenshot circles — which is a
            separate component from AdvertStorePreview's dialog. */}
        <div className="d-flex gap-3">
          <div
            role={canUpload ? 'button' : undefined}
            tabIndex={canUpload ? 0 : undefined}
            aria-label={canUpload ? (form.imageUrl ? 'Change the icon' : 'Upload an icon') : undefined}
            className="position-relative flex-shrink-0"
            style={{ width: 56, height: 56, cursor: canUpload ? 'pointer' : 'default' }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onFocus={() => setHover(true)}
            onBlur={() => setHover(false)}
            onClick={pick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                pick();
              }
            }}
          >
            <AppTile name={form.name} imageUrl={form.imageUrl} size={56} />
            {canUpload && (hover || uploading) ? (
              <div
                className="position-absolute top-0 start-0 d-flex align-items-center justify-content-center rounded text-white text-center"
                style={{ width: 56, height: 56, backgroundColor: 'rgba(0,0,0,.55)', fontSize: 10, lineHeight: 1.1 }}
              >
                {uploading ? <Spinner size="sm" /> : form.imageUrl ? 'Change icon' : 'Upload icon'}
              </div>
            ) : null}
            {canUpload ? (
              <input
                ref={input}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className="d-none"
                onChange={onUpload}
              />
            ) : null}
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="fw-semibold">{form.name || 'Untitled app'}</div>
            {form.author ? (
              <div className="text-muted" style={{ fontSize: 12 }}>
                by {form.author}
              </div>
            ) : null}
            {price(form.monthlyPrice) ? (
              <div className="mt-1" style={{ fontSize: 12 }}>
                {price(form.monthlyPrice)}
              </div>
            ) : null}
          </div>
        </div>
        {form.description ? <p className="mt-2 mb-1">{form.description}</p> : null}
        <div>
          {form.tags.map(tag => (
            <Badge key={tag} color="light" className="text-dark border me-1">
              {tag}
            </Badge>
          ))}
        </div>
        {canUpload ? (
          <div className="text-muted mt-2" style={{ fontSize: 12 }}>
            Hover the icon to upload a PNG, JPEG, GIF or WebP. Without one the listing shows the default tile.
            {form.imageUrl && onRemove ? (
              <Button color="link" size="sm" className="text-danger p-0 ms-2 align-baseline" disabled={disabled} onClick={onRemove}>
                Remove icon
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
};

export default AdvertPreview;
